import { useEffect, useState } from 'react';
import { AlertTriangle, FileText, Package } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import productApi from '@/lib/productApi';
import { inlineError, type InlineError } from '@/lib/errorReport';
import type { PublishImpact } from '@/types/product';

interface PublishDialogProps {
  productId: string;
  productName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublished: () => void;
  onPublish?: (reason: string) => Promise<void>;
}

/**
 * Publiceren is het enige moment waarop een wijziging zichtbaar wordt voor
 * offertes, dus het is ook het moment om te laten zien wat je raakt en om een
 * reden vast te leggen. Die reden is de history: zonder rollen in de catalogus
 * is dit de enige verantwoording achteraf.
 */
export function PublishDialog({
  productId,
  productName,
  open,
  onOpenChange,
  onPublished,
  onPublish,
}: PublishDialogProps) {
  const [impact, setImpact] = useState<PublishImpact | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<InlineError | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason('');
    productApi
      .publishImpact(productId)
      .then(setImpact)
      .catch(() => setImpact(null));
  }, [open, productId]);

  const handlePublish = async () => {
    setBusy(true);
    setError(null);
    try {
      if (onPublish) await onPublish(reason);
      else await productApi.publish(productId, reason);
      onOpenChange(false);
      onPublished();
    } catch (caught) {
      const response = (
        caught as { response?: { data?: { error?: string; issues?: Array<{ message: string }> } } }
      ).response?.data;
      setError(
        inlineError(
          response?.issues?.length
            ? `${response.error}: ${response.issues.map((issue) => issue.message).join('; ')}`
            : response?.error ||
                (caught instanceof Error ? caught.message : 'Publiceren is niet gelukt'),
          caught
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const quotes = impact?.quotes ?? [];
  const bundles = impact?.bundles ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{productName} publiceren</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {quotes.length > 0 && (
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                <strong>
                  {quotes.length} lopende offerte{quotes.length === 1 ? '' : 's'}
                </strong>{' '}
                gebruikt dit product. Die veranderen niet vanzelf: ze blijven op de versie staan
                waarmee ze gemaakt zijn en krijgen in de editor de melding &quot;product
                gewijzigd&quot;.
                <ul className="mt-2 space-y-1 text-sm">
                  {quotes.slice(0, 6).map((quote) => (
                    <li key={quote.id}>
                      {quote.companyName ? `${quote.companyName} — ` : ''}
                      {quote.title}{' '}
                      <span className="text-muted-foreground">
                        ({quote.status}, {quote.rows} regel{quote.rows === 1 ? '' : 's'})
                      </span>
                    </li>
                  ))}
                  {quotes.length > 6 && <li>en nog {quotes.length - 6} andere…</li>}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {bundles.length > 0 && (
            <Alert>
              <Package className="h-4 w-4" />
              <AlertDescription>
                Dit product zit in {bundles.length} bundel
                {bundles.length === 1 ? '' : 's'}: {bundles.map((bundle) => bundle.name).join(', ')}
                . Die bundels blijven op de vastgepinde versie staan tot je de melding
                &quot;onderdeel gewijzigd&quot; daar overneemt.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="publish-reason">Reden van deze wijziging</Label>
            <Textarea
              id="publish-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Bijvoorbeeld: uren voor Groot bijgewerkt na review, of prijsronde 2026."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Komt in de history te staan naast je naam en het tijdstip.
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error.message} <ErrorReportButton report={error.report} />
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Annuleren
          </Button>
          <Button onClick={handlePublish} disabled={busy}>
            {busy ? 'Bezig…' : 'Publiceren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PublishDialog;
